const cloneCalculations = calculations => calculations.map(calculation => ({ ...calculation }));

const makeCells = width => Array.from({ length: width }, () => '');

export function buildDivisionWorkedExample(problem, { includeVisualRepresentation = true, showBringArrow = false, formatLargeNumbers = false, explainTwoDigitDivisor = false } = {}) {
  const dividend = Math.max(0, Number(problem?.a) || 0);
  const divisor = Math.max(1, Number(problem?.b) || 1);
  const dividendDigits = String(dividend).split('').map(Number);
  const width = dividendDigits.length;
  const quotient = Math.floor(dividend / divisor);
  const quotientCells = makeCells(width);
  const display = value => formatLargeNumbers ? Number(value).toLocaleString('en-US') : String(value);
  const steps = [];
  const calculations = [];
  let remainder = 0;
  let segmentStart = 0;
  let started = false;
  let stepNumber = 0;

  const addStep = (title, message, data = {}) => {
    steps.push({
      id: data.id || 'step-' + steps.length,
      label: data.label || 'Paso ' + (++stepNumber),
      title,
      message,
      quotientCells: [...quotientCells],
      calculations: cloneCalculations(calculations),
      activeIndexes: data.activeIndexes || [],
      activeDivisor: Boolean(data.activeDivisor),
      activeQuotientIndex: Number.isInteger(data.activeQuotientIndex) ? data.activeQuotientIndex : null,
      activeCalculation: data.activeCalculation || null,
    });
  };

  addStep(
    'Observamos la división.',
    'Dividendo: ' + display(dividend) + '. Divisor: ' + display(divisor) + '.',
    { id: 'observe' },
  );

  dividendDigits.forEach((digit, index) => {
    const current = remainder * 10 + digit;

    if (!started && current < divisor && index < dividendDigits.length - 1) {
      const nextValue = current * 10 + dividendDigits[index + 1];
      addStep(
        'Tomamos las cifras necesarias del dividendo.',
        'Como ' + display(current) + ' es menor que ' + display(divisor) + ', tomamos ' + display(nextValue) + ' para poder dividir entre ' + display(divisor) + '.',
        {
          id: 'prepare-' + index,
          activeIndexes: [index, index + 1],
          activeDivisor: true,
        },
      );
      remainder = current;
      return;
    }

    started = true;
    const quotientDigit = Math.floor(current / divisor);
    quotientCells[index] = String(quotientDigit);
    const activeIndexes = Array.from({ length: index - segmentStart + 1 }, (_, offset) => segmentStart + offset);

    const product = quotientDigit * divisor;
    if (explainTwoDigitDivisor && divisor >= 10) {
      const nextQuotientDigit = quotientDigit + 1;
      const nextProduct = nextQuotientDigit * divisor;
      addStep(
        'Determinamos la cifra del cociente.',
        display(quotientDigit) + ' × ' + display(divisor) + ' = ' + display(product) +
          ' y ' + display(nextQuotientDigit) + ' × ' + display(divisor) + ' = ' +
          display(nextProduct) + ', que es mayor que ' + display(current) +
          '. Escribimos ' + display(quotientDigit) + ' en el cociente.',
        {
          id: 'determine-' + index,
          activeIndexes,
          activeDivisor: true,
          activeQuotientIndex: index,
        },
      );
    } else {
      addStep(
        'Dividimos.',
        display(current) + ' ÷ ' + display(divisor) + ' = ' + display(quotientDigit) + '. Escribimos ' + display(quotientDigit) + ' en el cociente.',
        {
          id: 'divide-' + index,
          activeIndexes,
          activeDivisor: true,
          activeQuotientIndex: index,
        },
      );
    }
    const calculation = {
      key: 'calculation-' + index,
      current,
      product,
      remainder: current - product,
      startIndex: segmentStart,
      endIndex: index,
      stage: 'product',
      broughtValue: null,
    };
    calculations.push(calculation);
    addStep(
      'Multiplicamos.',
      display(quotientDigit) + ' × ' + display(divisor) + ' = ' + display(product) + '. Escribimos ' + display(product) + ' debajo de ' + display(current) + '.',
      {
        id: 'multiply-' + index,
        activeIndexes,
        activeDivisor: true,
        activeQuotientIndex: index,
        activeCalculation: calculation.key,
      },
    );

    remainder = calculation.remainder;
    calculation.stage = 'subtract';
    addStep(
      'Restamos.',
      display(current) + ' − ' + display(product) + ' = ' + display(remainder) + '.',
      {
        id: 'subtract-' + index,
        activeIndexes,
        activeDivisor: true,
        activeQuotientIndex: index,
        activeCalculation: calculation.key,
      },
    );

    if (index < dividendDigits.length - 1) {
      const nextDigit = dividendDigits[index + 1];
      const lowered = remainder * 10 + nextDigit;
      calculation.broughtValue = lowered;
      // En Planetas 2–6 el paso "Bajamos" muestra qué cifra desciende y
      // exactamente en qué columna se une al residuo para formar el nuevo valor.
      if (showBringArrow) {
        calculation.broughtDigit = nextDigit;
        calculation.broughtColumn = index + 1;
      }
      addStep(
        'Bajamos la siguiente cifra.',
        'Bajamos el ' + nextDigit + '.',
        {
          id: 'bring-' + index,
          activeIndexes: [index + 1],
          activeDivisor: true,
          activeQuotientIndex: index,
          activeCalculation: calculation.key,
        },
      );
      segmentStart = index + 1;
    }
  });

  const finalRemainder = remainder;
  const resultNotation = finalRemainder === 0
    ? display(quotient)
    : display(quotient) + ' R ' + display(finalRemainder);

  addStep(
    'Resultado',
    'Entonces, ' + display(dividend) + ' ÷ ' + display(divisor) + ' = ' + resultNotation + '. El residuo es ' + display(finalRemainder) + '.',
    {
      id: 'result',
      label: 'Resultado',
      activeIndexes: [],
      activeDivisor: false,
      activeQuotientIndex: null,
    },
  );

  // Planeta 1 conserva este cierre visual. Los Planetas 2–6 terminan en
  // Resultado para concentrarse únicamente en el algoritmo de división larga.
  if (includeVisualRepresentation) {
    steps.push({
      id: 'visual-representation',
      label: 'Representación visual',
      title: 'Representación visual',
      message: display(dividend) + ' elementos repartidos en ' + display(divisor) + ' grupos iguales dan ' + display(quotient) + ' en cada grupo.',
      quotientCells: [...quotientCells],
      calculations: cloneCalculations(calculations),
      activeIndexes: [],
      activeDivisor: false,
      activeQuotientIndex: null,
      activeCalculation: null,
      isVisualRepresentation: true,
      groups: Array.from({ length: divisor }, (_, index) => ({
        label: index + 1,
        count: quotient,
        gems: Array.from({ length: quotient }, (_, gemIndex) => gemIndex + 1),
      })),
    });
  }

  return {
    kind: 'long-division-exact',
    dividend,
    divisor,
    quotient,
    dividendDigits: String(dividend),
    width,
    formatLargeNumbers,
    hasTwoDigitDivisor: divisor >= 10,
    steps,
  };
}
